import {
  DEFAULT_TIMESHEET_SETTINGS,
  TIME_ENTRY_BUCKETS,
  CLIENT_BILLING_MODES,
  type TimeEntryBucket,
  type ClientBillingMode,
  type TimesheetRounding,
  type TimesheetWeekStart,
  type TimesheetOvertimeSettings,
  type TimesheetSettings,
} from './types.js'

export function normalizeTimesheetSettings(raw: unknown): TimesheetSettings {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_TIMESHEET_SETTINGS }
  }
  const data = raw as Partial<TimesheetSettings>
  return {
    autoSources: {
      sessions: data.autoSources?.sessions === true,
      meetings: data.autoSources?.meetings === true,
      focus: data.autoSources?.focus === true,
    },
    weekStart: data.weekStart === 'SUNDAY' ? 'SUNDAY' : 'MONDAY',
    rounding:
      data.rounding === '5_MIN' ||
      data.rounding === '6_MIN' ||
      data.rounding === '15_MIN'
        ? data.rounding
        : 'NONE',
    overtime: {
      dailyMinutes:
        typeof data.overtime?.dailyMinutes === 'number' && data.overtime.dailyMinutes >= 0
          ? data.overtime.dailyMinutes
          : 0,
      weeklyMinutes:
        typeof data.overtime?.weeklyMinutes === 'number' && data.overtime.weeklyMinutes >= 0
          ? data.overtime.weeklyMinutes
          : 0,
      multiplier:
        typeof data.overtime?.multiplier === 'number' && data.overtime.multiplier > 0
          ? data.overtime.multiplier
          : 1.5,
    },
    expectedWeeklyMinutes:
      typeof data.expectedWeeklyMinutes === 'number' && data.expectedWeeklyMinutes >= 0
        ? data.expectedWeeklyMinutes
        : 2400,
    gps: data.gps === true,
    reminders: {
      submitReminder: data.reminders?.submitReminder === true,
      approvalReminder: data.reminders?.approvalReminder === true,
    },
  }
}

export interface ElementForPlannedHours {
  id: string
  type?: string
  parentId?: string | null
  checklist?: Array<{ estimatedHours?: number | null }> | null
}

export interface ChecklistItemForPlannedHours {
  parentContainerId?: string | null
  parentId?: string | null
  estimatedHours?: number | null
}

export function calculatePlannedHoursByGoal(
  elements: readonly ElementForPlannedHours[],
  checklistItems: readonly ChecklistItemForPlannedHours[] = [],
): Map<string, number> {
  const goals = elements.filter((el) => el.type === 'GOAL')
  const plannedByGoal = new Map<string, number>()
  if (goals.length === 0) return plannedByGoal

  const elementById = new Map<string, ElementForPlannedHours>()
  for (const el of elements) {
    elementById.set(el.id, el)
  }

  const goalMap = new Map<string, string>()
  const getGoalId = (id: string | null | undefined): string | null => {
    if (!id) return null
    if (goalMap.has(id)) return goalMap.get(id)!
    const visited = new Set<string>()
    let curr: string | null = id
    while (curr && !visited.has(curr)) {
      visited.add(curr)
      const el = elementById.get(curr)
      if (!el) break
      if (el.type === 'GOAL') {
        goalMap.set(id, el.id)
        return el.id
      }
      curr = el.parentId ?? null
    }
    goalMap.set(id, '')
    return null
  }

  for (const item of checklistItems) {
    const containerId = item.parentContainerId || item.parentId
    const gId = getGoalId(containerId)
    if (gId) {
      const est = Number(item.estimatedHours) || 0
      if (est > 0) {
        plannedByGoal.set(gId, (plannedByGoal.get(gId) || 0) + est)
      }
    }
  }

  for (const el of elements) {
    const gId = getGoalId(el.id)
    if (gId && Array.isArray(el.checklist)) {
      for (const item of el.checklist) {
        const est = Number(item.estimatedHours) || 0
        if (est > 0) {
          plannedByGoal.set(gId, (plannedByGoal.get(gId) || 0) + est)
        }
      }
    }
  }

  return plannedByGoal
}

const formatterCache = new Map<string, Intl.DateTimeFormat>()

function getCachedFormatter(tz: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${tz}::${options.hourCycle ?? ''}::${options.year ?? ''}::${options.month ?? ''}::${options.day ?? ''}::${options.hour ?? ''}`
  let f = formatterCache.get(key)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { ...options, timeZone: tz })
    formatterCache.set(key, f)
  }
  return f
}

/**
 * Returns the epoch milliseconds of local midnight (00:00:00) on dateOnly (YYYY-MM-DD)
 * in the specified time zone. DST-safe. If tz is invalid, falls back to UTC.
 */
export function zonedDayStartMs(dateOnly: string, tz: string = 'UTC'): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateOnly).trim())
  if (!m) {
    const d = new Date(dateOnly)
    return isNaN(d.getTime()) ? 0 : d.getTime()
  }
  const targetYear = parseInt(m[1], 10)
  const targetMonth = parseInt(m[2], 10)
  const targetDay = parseInt(m[3], 10)

  let resolvedTz = 'UTC'
  if (typeof tz === 'string' && tz.trim()) {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: tz.trim() })
      resolvedTz = tz.trim()
    } catch {
      resolvedTz = 'UTC'
    }
  }

  const formatter = getCachedFormatter(resolvedTz, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hourCycle: 'h23',
  })

  const targetAsUtc = Date.UTC(targetYear, targetMonth - 1, targetDay, 0, 0, 0, 0)
  let t = targetAsUtc

  for (let iter = 0; iter < 3; iter++) {
    const parts = formatter.formatToParts(new Date(t))
    let y = 0, mo = 0, day = 0, h = 0, mi = 0, s = 0
    for (const p of parts) {
      if (p.type === 'year') y = parseInt(p.value, 10)
      if (p.type === 'month') mo = parseInt(p.value, 10)
      if (p.type === 'day') day = parseInt(p.value, 10)
      if (p.type === 'hour') h = parseInt(p.value, 10) % 24
      if (p.type === 'minute') mi = parseInt(p.value, 10)
      if (p.type === 'second') s = parseInt(p.value, 10)
    }
    const localAsUtc = Date.UTC(y, mo - 1, day, h, mi, s, 0)
    const diff = localAsUtc - targetAsUtc
    if (diff === 0) break
    t -= diff
  }
  return t
}

function getZonedParts(date: Date, tz: string): { year: number; month: number; day: number; dayOfWeek: number } {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      weekday: 'short',
    })
    const parts = formatter.formatToParts(date)
    let year = date.getUTCFullYear()
    let month = date.getUTCMonth() + 1
    let day = date.getUTCDate()
    let dayOfWeek = date.getUTCDay()
    const wMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
    for (const p of parts) {
      if (p.type === 'year') year = parseInt(p.value, 10)
      if (p.type === 'month') month = parseInt(p.value, 10)
      if (p.type === 'day') day = parseInt(p.value, 10)
      if (p.type === 'weekday') {
        if (p.value in wMap) dayOfWeek = wMap[p.value]
      }
    }
    return { year, month, day, dayOfWeek }
  } catch {
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
      dayOfWeek: date.getUTCDay(),
    }
  }
}

/**
 * Computes an ISO-like week identifier `YYYY-Www` for a given date in the member's time zone,
 * respecting the workspace week start day (MONDAY or SUNDAY).
 */
export function weekIdFor(
  dateInput: Date | string | number,
  tz: string = 'UTC',
  weekStart: TimesheetWeekStart = 'MONDAY',
): string {
  const date = typeof dateInput === 'object' && dateInput instanceof Date
    ? dateInput
    : new Date(dateInput)

  const { year, month, day } = getZonedParts(date, tz)

  if (weekStart === 'MONDAY') {
    // ISO 8601: Monday=0, Sunday=6
    const target = new Date(Date.UTC(year, month - 1, day))
    const dayNr = (target.getUTCDay() + 6) % 7 // Mon=0 .. Sun=6
    target.setUTCDate(target.getUTCDate() - dayNr + 3) // Nearest Thursday determines week year
    const firstThursday = target.getTime()
    target.setUTCMonth(0, 1)
    if (target.getUTCDay() !== 4) {
      target.setUTCMonth(0, 1 + ((4 - target.getUTCDay() + 7) % 7))
    }
    const weekNumber = 1 + Math.ceil((firstThursday - target.getTime()) / (7 * 86400000))
    const weekYear = new Date(firstThursday).getUTCFullYear()
    return `${weekYear}-W${String(weekNumber).padStart(2, '0')}`
  } else {
    // Sunday start: Sun=0 .. Sat=6
    // Wednesday determines the week year
    const target = new Date(Date.UTC(year, month - 1, day))
    const dayNr = target.getUTCDay() // Sun=0 .. Sat=6
    target.setUTCDate(target.getUTCDate() - dayNr + 3) // Nearest Wednesday determines week year
    const firstWednesday = target.getTime()
    target.setUTCMonth(0, 1)
    if (target.getUTCDay() !== 3) {
      target.setUTCMonth(0, 1 + ((3 - target.getUTCDay() + 7) % 7))
    }
    const weekNumber = 1 + Math.ceil((firstWednesday - target.getTime()) / (7 * 86400000))
    const weekYear = new Date(firstWednesday).getUTCFullYear()
    return `${weekYear}-W${String(weekNumber).padStart(2, '0')}`
  }
}

/**
 * Calculates rounded minutes between two timestamps.
 */
export function minutesBetween(
  start: Date | string | number,
  end: Date | string | number,
  rounding: TimesheetRounding = 'NONE',
): number {
  const s = typeof start === 'number' ? start : new Date(start).getTime()
  const e = typeof end === 'number' ? end : new Date(end).getTime()
  if (isNaN(s) || isNaN(e) || e <= s) return 0
  const rawMinutes = (e - s) / (60 * 1000)
  switch (rounding) {
    case '5_MIN':
      return Math.round(rawMinutes / 5) * 5
    case '6_MIN':
      return Math.round(rawMinutes / 6) * 6
    case '15_MIN':
      return Math.round(rawMinutes / 15) * 15
    case 'NONE':
    default:
      return Math.round(rawMinutes)
  }
}

/**
 * Splits daily minutes into regular and overtime totals based on workspace overtime settings.
 */
export function splitOvertime(
  dailyMinutes: number[],
  settings: TimesheetOvertimeSettings,
): { regularMinutes: number; overtimeMinutes: number } {
  const dailyThreshold = settings?.dailyMinutes && settings.dailyMinutes > 0 ? settings.dailyMinutes : 0
  const weeklyThreshold = settings?.weeklyMinutes && settings.weeklyMinutes > 0 ? settings.weeklyMinutes : 0

  let totalDailyOvertime = 0
  let totalPotentialRegular = 0

  for (const dayMinutes of dailyMinutes) {
    const mins = Math.max(0, dayMinutes)
    if (dailyThreshold > 0 && mins > dailyThreshold) {
      totalDailyOvertime += (mins - dailyThreshold)
      totalPotentialRegular += dailyThreshold
    } else {
      totalPotentialRegular += mins
    }
  }

  let finalRegular = totalPotentialRegular
  let weeklyOvertime = 0

  if (weeklyThreshold > 0 && totalPotentialRegular > weeklyThreshold) {
    weeklyOvertime = totalPotentialRegular - weeklyThreshold
    finalRegular = weeklyThreshold
  }

  return {
    regularMinutes: finalRegular,
    overtimeMinutes: totalDailyOvertime + weeklyOvertime,
  }
}

export function isTimeEntryBucket(val: unknown): val is TimeEntryBucket {
  return typeof val === 'string' && (TIME_ENTRY_BUCKETS as readonly string[]).includes(val)
}

export function isClientBillingMode(val: unknown): val is ClientBillingMode {
  return typeof val === 'string' && (CLIENT_BILLING_MODES as readonly string[]).includes(val)
}

export interface DeriveTimeEntryBucketInput {
  clientId?: string | null
  clientBillingMode?: ClientBillingMode | string | null
  billable?: boolean | null
}

/**
 * Pure function to derive time entry bucket from client link, client billing mode, and billable flag:
 * - no client → INTERNAL
 * - HOURLY: billable → CLIENT_BILLABLE, else CLIENT_NON_BILLABLE
 * - FIXED_BID: billable → FIXED_BID, else CLIENT_NON_BILLABLE
 * - NON_BILLABLE → CLIENT_NON_BILLABLE
 */
export function deriveTimeEntryBucket(input: DeriveTimeEntryBucketInput): TimeEntryBucket {
  const { clientId, clientBillingMode, billable } = input
  if (!clientId || !clientId.trim()) {
    return 'INTERNAL'
  }

  const mode: ClientBillingMode =
    clientBillingMode && isClientBillingMode(clientBillingMode)
      ? clientBillingMode
      : 'HOURLY'

  const isBillable = billable !== false

  if (mode === 'HOURLY') {
    return isBillable ? 'CLIENT_BILLABLE' : 'CLIENT_NON_BILLABLE'
  }

  if (mode === 'FIXED_BID') {
    return isBillable ? 'FIXED_BID' : 'CLIENT_NON_BILLABLE'
  }

  if (mode === 'NON_BILLABLE') {
    return 'CLIENT_NON_BILLABLE'
  }

  return 'INTERNAL'
}

/**
 * Returns true if the time entry should count toward daily/weekly/item totals.
 * Excludes REJECTED entries and unconfirmed overlapping auto drafts.
 */
export function countsInTotals(entry: {
  auto?: boolean | null
  overlaps?: boolean | null
  autoConfirmed?: boolean | null
  status?: string | null
}): boolean {
  if (entry.status === 'REJECTED') {
    return false
  }
  if (entry.auto === true && entry.overlaps === true && !entry.autoConfirmed) {
    return false
  }
  return true
}

/**
 * Returns true if the entry is eligible for submission in a week submit action.
 * Must be DRAFT or REJECTED, and cannot be an unconfirmed auto draft.
 */
export function isSubmittable(entry: {
  auto?: boolean | null
  autoConfirmed?: boolean | null
  status?: string | null
}): boolean {
  if (entry.status !== 'DRAFT' && entry.status !== 'REJECTED') {
    return false
  }
  if (entry.auto === true && !entry.autoConfirmed) {
    return false
  }
  return true
}


